import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { KeyRound, Menu, X } from "lucide-react";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Toaster } from "../components/ui/sonner";
import { canCreate } from "../features/incidents/permissions";
import { canManageUsers } from "../features/admin-users/permissions";
import { canManageMasterData } from "../features/admin-master-data/permissions";
import { ReminderBanner } from "../features/notifications/reminder-banner";
import { UserMenu } from "../features/account/user-menu";
import { SessionExpiryBanner } from "../components/session-expiry-banner";
import { BrandLogo } from "../components/brand-logo";

/**
 * Authenticated app shell (M-002/M-004/M-005/M-006): desktop gets a fixed left
 * sidebar - wordmark on top, nav with the orange left-accent on the active
 * item, user card pinned at the bottom; mobile gets a topbar whose burger
 * toggles the same nav as a full-width panel. The nav is role-aware for
 * display only (server re-checks, ADR-API-09).
 */
export function AppShell({ children }: { children: ReactNode }): JSX.Element {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = (): void => setMenuOpen(false);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Desktop (md+): fixed left sidebar. */}
      <aside className="fixed inset-y-0 left-0 hidden w-60 flex-col border-r bg-background md:flex">
        <div className="px-4 py-5">
          <Link to="/dashboard" data-testid="sidebar_brand">
            <BrandLogo className="h-7" />
          </Link>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto py-2">
          <ShellNav placement="sidebar" />
        </nav>
        <UserMenu placement="sidebar" />
      </aside>

      {/* Mobile (<md): topbar; the burger toggles the nav panel. */}
      <header className="sticky top-0 z-20 border-b bg-background md:hidden">
        <div className="flex items-center justify-between px-4 py-3">
          <Link to="/dashboard" onClick={closeMenu} data-testid="topbar_brand">
            <BrandLogo className="h-7" />
          </Link>
          <Button
            data-testid="topbar_menu-toggle"
            variant="ghost"
            size="icon"
            aria-label={t("nav.menu")}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? (
              <X aria-hidden="true" />
            ) : (
              <Menu aria-hidden="true" />
            )}
          </Button>
        </div>
        {menuOpen ? (
          <div className="border-t">
            <nav className="space-y-1 py-2">
              <ShellNav placement="topbar" onNavigate={closeMenu} />
            </nav>
            <UserMenu placement="topbar" />
          </div>
        ) : null}
      </header>

      <div className="md:pl-60">
        <main className="mx-auto max-w-6xl px-4 py-6">
          {/* F-014/AC-015: pending reminders show above the content on every
           * screen after login; renders nothing while there are none. */}
          <SessionExpiryBanner />
          <SystemAccountNotice />
          <ReminderBanner />
          {children}
        </main>
      </div>
      <Toaster />
    </div>
  );
}

/**
 * The system account is signed in ([F-031]): it administers accounts and
 * works on no content, and its session ends with the elevation window. Said
 * on every page it can open, so nobody mistakes it for a personal login.
 */
function SystemAccountNotice(): JSX.Element | null {
  const { t } = useTranslation();
  const { isSystem } = useAuth();
  if (!isSystem) return null;
  return (
    <div
      data-testid="shell_system-notice"
      role="note"
      className="mb-4 flex items-start gap-3 rounded-md border border-sky-300 bg-sky-50 p-4 text-sm text-sky-950 dark:border-sky-800 dark:bg-sky-950 dark:text-sky-100"
    >
      <KeyRound className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
      <div>
        <p className="font-medium">{t("system.noticeTitle")}</p>
        <p>{t("system.noticeBody")}</p>
      </div>
    </div>
  );
}

/**
 * Where a shell part renders. The sidebar stays in the DOM on mobile (hidden by
 * CSS), so an open burger menu renders the nav a second time - each placement
 * therefore carries its own `data-testid` area.
 */
type Placement = "sidebar" | "topbar";

/**
 * The nav entries, shared by sidebar and mobile panel. The system account
 * sees the administration only ([F-031]).
 */
function ShellNav({
  placement,
  onNavigate,
}: {
  placement: Placement;
  onNavigate?: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const { can, hasRole, user, isSystem } = useAuth();
  const inSidebar = placement === "sidebar";
  const administers = canManageUsers(hasRole, isSystem);

  if (isSystem) {
    return <AdminNav placement={placement} onNavigate={onNavigate} />;
  }

  return (
    <>
      <ShellNavLink
        to="/dashboard"
        testid={inSidebar ? "sidebar_nav-dashboard" : "topbar_nav-dashboard"}
        label={t("nav.dashboard")}
        onNavigate={onNavigate}
      />
      {/* F-023: the Trend-Dashboard, for whoever holds `dashboard.trends`. */}
      {can("dashboard.trends") ? (
        <ShellNavLink
          to="/trends"
          testid={inSidebar ? "sidebar_nav-trends" : "topbar_nav-trends"}
          label={t("nav.trends")}
          onNavigate={onNavigate}
        />
      ) : null}
      <ShellNavLink
        to="/incidents"
        testid={inSidebar ? "sidebar_nav-incidents" : "topbar_nav-incidents"}
        label={t("nav.incidents")}
        onNavigate={onNavigate}
      />
      {canCreate(user) ? (
        <ShellNavLink
          to="/incidents/new"
          testid={inSidebar ? "sidebar_nav-create" : "topbar_nav-create"}
          label={t("nav.createIncident")}
          onNavigate={onNavigate}
        />
      ) : null}
      {/* Betriebslog (Hauptansicht 5): every role reads it (PO-050). */}
      <ShellNavLink
        to="/operations-log"
        testid={inSidebar ? "sidebar_nav-operations-log" : "topbar_nav-operations-log"}
        label={t("nav.operationsLog")}
        onNavigate={onNavigate}
      />
      {/* M-008: visible to EVERY authenticated user - Tab "Meine Daten" is
       * self-service (Q-010); the management tab gates itself inside the
       * page. */}
      <ShellNavLink
        to="/dsgvo"
        testid={inSidebar ? "sidebar_nav-privacy" : "topbar_nav-privacy"}
        label={t("nav.privacy")}
        onNavigate={onNavigate}
      />
      {/* M-006: Administrator only - display gating, the server re-checks
       * every account route ([F-028]). */}
      {administers ? <AdminNav placement={placement} onNavigate={onNavigate} /> : null}
      {/* F-009: Administrator only - Stammdatenverwaltung sits in the
       * Administrationsbereich next to Benutzer & Rollen (server re-checks). */}
      {canManageMasterData(user) ? (
        <ShellNavLink
          to="/admin/master-data"
          testid={inSidebar ? "sidebar_nav-master-data" : "topbar_nav-master-data"}
          label={t("nav.masterData")}
          onNavigate={onNavigate}
        />
      ) : null}
    </>
  );
}

/** The account administration: accounts, the installation's status, the journal. */
function AdminNav({ placement, onNavigate }: { placement: Placement; onNavigate?: () => void }): JSX.Element {
  const { t } = useTranslation();
  const inSidebar = placement === "sidebar";
  return (
    <>
      <ShellNavLink
        to="/admin/users"
        testid={inSidebar ? "sidebar_nav-users" : "topbar_nav-users"}
        label={t("nav.users")}
        onNavigate={onNavigate}
      />
      <ShellNavLink
        to="/admin/status"
        testid={inSidebar ? "sidebar_nav-status" : "topbar_nav-status"}
        label={t("nav.status")}
        onNavigate={onNavigate}
      />
      <ShellNavLink
        to="/admin/journal"
        testid={inSidebar ? "sidebar_nav-journal" : "topbar_nav-journal"}
        label={t("nav.journal")}
        onNavigate={onNavigate}
      />
    </>
  );
}

function ShellNavLink({
  to,
  testid,
  label,
  onNavigate,
}: {
  to:
    | "/dashboard"
    | "/incidents"
    | "/incidents/new"
    | "/operations-log"
    | "/dsgvo"
    | "/admin/users"
    | "/admin/status"
    | "/admin/journal"
    | "/admin/master-data"
    | "/trends";
  testid: string;
  label: string;
  onNavigate?: () => void;
}): JSX.Element {
  return (
    <Link
      to={to}
      data-testid={testid}
      onClick={onNavigate}
      className="flex min-h-11 items-center border-l-2 px-4 text-sm transition-colors md:min-h-9"
      activeProps={{
        className: "border-primary-text bg-primary/5 font-medium text-foreground",
      }}
      inactiveProps={{
        className:
          "border-transparent text-muted-foreground hover:bg-accent hover:text-foreground",
      }}
    >
      {label}
    </Link>
  );
}
